// A popout tooltip (what site.ts wires up on the Razor pages): clicking the header shows its section under it, fading
// in, and hides any other; clicking it again, or anywhere outside a popout, hides it. The navbar's dropdown is one.
// Opening one scrolls to it: to the top of the page for the navbar's, else so the section is centred.

import { type CSSProperties, type KeyboardEvent, type ReactNode, useId, useLayoutEffect, useRef, useSyncExternalStore } from "react";
import { createStore } from "@core/store";

const FADE_IN_MS = 400; // jQuery's fadeIn default, as in site.ts

/** The open popout's id; at most one is open. */
const openPopout = createStore<string | null>(null);

/** Hides the open popout (the app does this on every navigation; on Razor pages the page load did). */
export function closePopouts(): void {
    openPopout.set(null);
}

let mounted = 0;

function closeOnOutsideClick(event: MouseEvent): void {
    if (!(event.target instanceof Element) || !event.target.closest(".popout-tooltip"))
        closePopouts();
}

/** One document listener while any popout is mounted. */
function usePopoutListener(): void {
    useLayoutEffect(() => {
        if (mounted++ === 0)
            document.addEventListener("click", closeOnOutsideClick);

        return () => {
            if (--mounted === 0)
                document.removeEventListener("click", closeOnOutsideClick);
        };
    }, []);
}

export interface PopoutTooltipProps {
    /** The popout's id, for links to it ("/Study/Spades#finesse"). */
    id?: string;
    /** Classes after "popout-tooltip" (e.g. "d-inline-block"). */
    className?: string;
    header: ReactNode;
    headerStyle?: CSSProperties;
    /** The section's content. */
    children: ReactNode;
}

export function PopoutTooltip({ id: elementId, className, header, headerStyle, children }: PopoutTooltipProps) {
    const id = useId();
    const open = useSyncExternalStore(openPopout.subscribe, () => openPopout.get() === id, () => false);
    const section = useRef<HTMLElement>(null);
    const headerRef = useRef<HTMLElement>(null);
    const wasOpen = useRef(false);

    usePopoutListener();

    const toggle = () => openPopout.set(current => current === id ? null : id);

    /** Enter or Space on a header that says it's a button (the navbar's menu) opens it as a click would. */
    function toggleFromKeyboard(event: KeyboardEvent): void {
        if ((event.key === "Enter" || event.key === " ") && event.target instanceof Element && event.target.getAttribute("role") === "button") {
            event.preventDefault();
            toggle();
        }
    }

    useLayoutEffect(() => {
        const element = section.current!;
        const opened = open && !wasOpen.current;
        wasOpen.current = open;

        if (!opened)
            return;

        element.animate?.([{ opacity: 0 }, { opacity: 1 }], { duration: FADE_IN_MS, easing: "ease" });

        if (headerRef.current!.closest("#site-nav")) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
        else {
            const top = element.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({ top: top - (window.innerHeight - element.offsetHeight) / 2, behavior: "smooth" });
        }
    }, [open]);

    return (
        <div id={elementId} className={className ? `popout-tooltip ${className}` : "popout-tooltip"}>
            <header ref={headerRef} style={headerStyle} onClick={toggle} onKeyDown={toggleFromKeyboard}>
                {header}
            </header>
            <section ref={section} style={open ? { display: "block" } : undefined}>
                {children}
            </section>
        </div>
    );
}
